import React, { useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Camera, Send } from "lucide-react";
import { useAuthStore } from "../../../store/authStore";
import { supportTicketSchema } from "../schemas/supportTicket.schema";
import { Modal, Button, FormInput, FormSelect, FormTextarea } from "../../../components/ui";
import { toast } from "react-hot-toast";
import api from "../../../services/api";

export default function SupportTicketModal({ isOpen, onClose }) {
  const { user } = useAuthStore();
  const fileInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setValue
  } = useForm({
    resolver: zodResolver(supportTicketSchema),
    defaultValues: {
      category: "Technical Issue",
      phone: user?.phone || user?.phoneNumber || "",
      description: "",
    },
  });

  const onSubmit = async (data) => {
    try {
      const formData = new FormData();
      formData.append("category", data.category);
      formData.append("description", data.description);
      formData.append(
        "userData",
        JSON.stringify({
          name: user?.name,
          email: user?.email,
          phone: data.phone,
          role: user?.role,
        })
      );

      if (fileInputRef.current?.files) {
        Array.from(fileInputRef.current.files).forEach((file) => {
          formData.append("files", file);
        });
      }

      await api.post("/support/ticket", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Ticket submitted successfully! We will contact you soon.");
      reset();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to submit ticket. Please try again.");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Raise a Technical Support Ticket"
      size="lg"
      outsideClose={false}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-4">
            <FormSelect
              label="Issue Category"
              field="category"
              options={[
                { value: "Technical Issue", label: "Technical Issue" },
                { value: "User Credential Problem", label: "User Credential Problem" },
                { value: "Module Error", label: "Module Error" },
                { value: "Feature Request", label: "Feature Request" },
                { value: "Billing/Accounts Query", label: "Billing/Accounts Query" },
                { value: "Other", label: "Other" },
              ]}
              error={errors.category?.message}
              {...register("category")}
            />

            <FormInput
              label="Contact Phone Number"
              field="phone"
              type="tel"
              placeholder="10-digit phone number"
              error={errors.phone?.message}
              {...register("phone", {
                onChange: (e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  setValue("phone", val);
                }
              })}
            />

            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
                Raise Issue By (User Details)
              </label>
              <div className="space-y-2.5 p-3 bg-slate-50 border border-slate-100 rounded-lg">
                <div className="flex items-center justify-between text-xs font-bold text-slate-700">
                  <span className="text-slate-400">Name:</span>
                  <span>{user?.name}</span>
                </div>
                <div className="flex items-center justify-between text-xs font-bold text-slate-700">
                  <span className="text-slate-400">Role:</span>
                  <span className="uppercase text-[10px] bg-brand-blue/10 text-brand-blue px-1.5 py-0.5 rounded leading-none">
                    {user?.role}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs font-bold text-slate-700">
                  <span className="text-slate-400">Email:</span>
                  <span>{user?.email}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4 h-full flex flex-col">
            <FormTextarea
              label="Issue Description"
              field="description"
              placeholder="Describe the issue you're facing..."
              error={errors.description?.message}
              rows={8}
              className="resize-none"
              {...register("description")}
            />
          </div>
        </div>

        <div className="p-4 bg-blue-50/50 border border-brand-blue/10 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-brand-blue shadow-sm border border-brand-blue/10">
              <Camera size={14} />
            </div>
            <div className="text-xs">
              <div className="font-bold text-brand-dark">Screenshots</div>
              <div className="text-slate-500 font-medium tracking-tight">
                Attach pictures for faster resolution
              </div>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="text-[11px] text-slate-500 file:mr-4 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:text-[10px] file:font-black file:bg-brand-blue file:text-white hover:file:bg-brand-dark transition-all cursor-pointer"
          />
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="secondary"
            fullWidth
            onClick={onClose}
          >
            Discard
          </Button>
          <Button
            type="submit"
            variant="primary"
            fullWidth
            loading={isSubmitting}
            rightIcon={<Send size={16} />}
          >
            Submit Ticket
          </Button>
        </div>
      </form>
    </Modal>
  );
}
